/**
 * IndexedDB wrapper for offline data storage.
 * Uses the `idb` library for promise-based IndexedDB access.
 */
import { openDB, IDBPDatabase, DBSchema } from 'idb';

const DB_NAME = 'panluma_offline_v1';
const DB_VERSION = 2;

/** Status of a queued offline mutation awaiting replay. */
export type MutationStatus = 'pending' | 'syncing' | 'failed';

/**
 * A single offline mutation persisted to the outbox. Replayed in `created_at`
 * order when connectivity returns. `dedupe_key` coalesces repeated edits to the
 * same logical target (last-write-wins); `temp_id` is the client-minted id of an
 * entity this mutation creates, remapped to the server id after replay.
 */
export interface MutationQueueEntry {
  id: string;
  type: string;
  payload: unknown;
  dedupe_key: string | null;
  temp_id: string | null;
  status: MutationStatus;
  retries: number;
  error: string | null;
  created_at: number;
  updated_at: number;
}

interface OfflineDBSchema extends DBSchema {
  metadata: {
    key: string;
    value: {
      key: string;
      value: unknown;
      updated_at: number;
    };
  };
  query_cache: {
    key: string;
    value: {
      key: string;
      data: unknown;
      updated_at: number;
    };
  };
  mutation_queue: {
    key: string;
    value: MutationQueueEntry;
    indexes: { created_at: number };
  };
}

class OfflineStorageService {
  private db: IDBPDatabase<OfflineDBSchema> | null = null;
  private initPromise: Promise<IDBPDatabase<OfflineDBSchema>> | null = null;

  /**
   * Initialize the IndexedDB database.
   * Safe to call multiple times - will return existing connection.
   */
  async initialize(): Promise<IDBPDatabase<OfflineDBSchema>> {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Metadata store for sync state, user context, etc.
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }

        // Query cache store for React Query persistence
        if (!db.objectStoreNames.contains('query_cache')) {
          db.createObjectStore('query_cache', { keyPath: 'key' });
        }

        // Outbox for offline mutations, replayed in created_at order on reconnect
        if (!db.objectStoreNames.contains('mutation_queue')) {
          const store = db.createObjectStore('mutation_queue', { keyPath: 'id' });
          store.createIndex('created_at', 'created_at');
        }
      },
      blocked() {
        // An older-version connection in another tab is holding this open()
        // hostage, and it will stay pending for as long as that tab lives —
        // Safari keeps restored tabs around across launches, so "as long as
        // that tab lives" can be weeks. Nothing to do here but say so; the
        // app-critical wait (the React Query cache restore, which gates every
        // fetch in the app) is bounded by RESTORE_TIMEOUT_MS in queryPersister.
        console.warn('IndexedDB blocked - another tab has an older version open');
      },
      blocking: () => {
        // The mirror image: a NEWER version wants to upgrade and this tab's
        // connection is what's blocking it. Close it — holding on wedges the
        // other tab's open() indefinitely (see `blocked` above). Our own reads
        // reopen on demand via getDb().
        console.warn('IndexedDB blocking - closing this connection so the upgrade can proceed');
        this.close();
      },
      terminated: () => {
        // The browser killed the connection out from under us. The handle is
        // dead — drop it so the next read opens a fresh one instead of issuing
        // requests that will never complete.
        console.error('IndexedDB connection terminated unexpectedly');
        this.close();
      },
    });

    this.db = await this.initPromise;
    return this.db;
  }

  /**
   * Drop this tab's connection and forget it, so the next operation opens a
   * fresh one. Used when the handle is no longer usable (terminated) or must
   * not be held (it is blocking another tab's version upgrade).
   */
  close(): void {
    this.db?.close();
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Get the database instance, initializing if needed.
   */
  private async getDb(): Promise<IDBPDatabase<OfflineDBSchema>> {
    if (!this.db) {
      await this.initialize();
    }
    return this.db!;
  }

  // ============ Metadata Operations ============

  /**
   * Get the last sync timestamp.
   */
  async getLastSyncTime(): Promise<Date | null> {
    try {
      const db = await this.getDb();
      const record = await db.get('metadata', 'last_sync_time');
      if (record?.value && typeof record.value === 'number') {
        return new Date(record.value);
      }
      return null;
    } catch (error) {
      console.error('Failed to get last sync time:', error);
      return null;
    }
  }

  /**
   * Set the last sync timestamp.
   */
  async setLastSyncTime(time: Date): Promise<void> {
    try {
      const db = await this.getDb();
      await db.put('metadata', {
        key: 'last_sync_time',
        value: time.getTime(),
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Failed to set last sync time:', error);
    }
  }

  /**
   * Get the current tenant ID stored for cache isolation.
   */
  async getCurrentTenantId(): Promise<string | null> {
    try {
      const db = await this.getDb();
      const record = await db.get('metadata', 'current_tenant_id');
      return (record?.value as string) || null;
    } catch (error) {
      console.error('Failed to get current tenant ID:', error);
      return null;
    }
  }

  /**
   * Set the current tenant ID for cache isolation.
   */
  async setCurrentTenantId(tenantId: string): Promise<void> {
    try {
      const db = await this.getDb();
      await db.put('metadata', {
        key: 'current_tenant_id',
        value: tenantId,
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Failed to set current tenant ID:', error);
    }
  }

  /**
   * The `access_version` the last `/tasks/sync` response carried.
   *
   * Sent back on every incremental sync so the server can tell whether this
   * cache was built under the caller's CURRENT workspace/board grants. Null
   * means "no idea" — a fresh install, or a cache written before the endpoint
   * was board-scoped — and the server answers that with a full resync, which is
   * the point: a pre-upgrade cache holds rows from boards the user never had a
   * grant on.
   */
  async getSyncAccessVersion(): Promise<string | null> {
    try {
      const db = await this.getDb();
      const record = await db.get('metadata', 'sync_access_version');
      return (record?.value as string) || null;
    } catch (error) {
      console.error('Failed to get sync access version:', error);
      return null;
    }
  }

  /**
   * Store the `access_version` from a COMPLETED sync run.
   *
   * Only ever written beside the sync watermark, and for the same reason: a run
   * cut short has not fully rebuilt the cache, so recording the version would
   * claim a reconciliation that never finished.
   */
  async setSyncAccessVersion(version: string): Promise<void> {
    try {
      const db = await this.getDb();
      await db.put('metadata', {
        key: 'sync_access_version',
        value: version,
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Failed to set sync access version:', error);
    }
  }

  // ============ Query Cache Operations ============

  /**
   * Get a cached query result.
   */
  async getQueryCache(key: string): Promise<unknown | null> {
    try {
      const db = await this.getDb();
      const record = await db.get('query_cache', key);
      return record?.data ?? null;
    } catch (error) {
      console.error('Failed to get query cache:', error);
      return null;
    }
  }

  /**
   * Set a cached query result.
   */
  async setQueryCache(key: string, data: unknown): Promise<void> {
    try {
      const db = await this.getDb();
      await db.put('query_cache', {
        key,
        data,
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Failed to set query cache:', error);
    }
  }

  /**
   * Delete a cached query result.
   */
  async deleteQueryCache(key: string): Promise<void> {
    try {
      const db = await this.getDb();
      await db.delete('query_cache', key);
    } catch (error) {
      console.error('Failed to delete query cache:', error);
    }
  }

  /**
   * Clear all query cache entries.
   */
  async clearQueryCache(): Promise<void> {
    try {
      const db = await this.getDb();
      await db.clear('query_cache');
    } catch (error) {
      console.error('Failed to clear query cache:', error);
    }
  }

  // ============ Mutation Queue (Outbox) Operations ============

  /**
   * List all queued mutations in replay order (oldest first).
   */
  async listMutations(): Promise<MutationQueueEntry[]> {
    try {
      const db = await this.getDb();
      return await db.getAllFromIndex('mutation_queue', 'created_at');
    } catch (error) {
      console.error('Failed to list mutations:', error);
      return [];
    }
  }

  /**
   * Find the pending mutation matching a dedupe key (for coalescing edits).
   */
  async findPendingMutationByDedupeKey(
    dedupeKey: string,
  ): Promise<MutationQueueEntry | null> {
    const entries = await this.listMutations();
    return (
      entries.find(
        (e) => e.dedupe_key === dedupeKey && e.status !== 'syncing',
      ) ?? null
    );
  }

  /**
   * Insert or replace a queued mutation.
   */
  async putMutation(entry: MutationQueueEntry): Promise<void> {
    const db = await this.getDb();
    await db.put('mutation_queue', entry);
  }

  /**
   * Remove a queued mutation (after successful replay or discard).
   */
  async deleteMutation(id: string): Promise<void> {
    try {
      const db = await this.getDb();
      await db.delete('mutation_queue', id);
    } catch (error) {
      console.error('Failed to delete mutation:', error);
    }
  }

  /**
   * Remove all queued mutations.
   */
  async clearMutations(): Promise<void> {
    try {
      const db = await this.getDb();
      await db.clear('mutation_queue');
    } catch (error) {
      console.error('Failed to clear mutations:', error);
    }
  }

  // ============ Data Management ============

  /**
   * Clear all offline data.
   * Call this on logout or tenant switch.
   */
  async clearAllData(): Promise<void> {
    try {
      const db = await this.getDb();
      await Promise.all([
        db.clear('metadata'),
        db.clear('query_cache'),
        db.clear('mutation_queue'),
      ]);
    } catch (error) {
      console.error('Failed to clear all data:', error);
    }
  }

  /**
   * Check available storage quota.
   */
  async getStorageEstimate(): Promise<{ used: number; available: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          used: estimate.usage || 0,
          available: estimate.quota || 0,
        };
      } catch (error) {
        console.error('Failed to get storage estimate:', error);
      }
    }
    return { used: 0, available: Infinity };
  }

  /**
   * Check if IndexedDB is available.
   */
  static isAvailable(): boolean {
    try {
      return 'indexedDB' in window && indexedDB !== null;
    } catch {
      return false;
    }
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorageService();

export { OfflineStorageService };
